// Selector de fechas del Pulso (Pau 08-10-2026; diseño «la 3 con cara de Apple»): una barra con los atajos de un
// clic, el periodo con flechas ‹ › para ir al anterior o al siguiente y, al pulsar el periodo, un calendario de dos
// meses con «Ir a» por meses y los días raros marcados. Un rango libre son 4 clics: abrir, primer día, último, Ver.
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import {
  ATAJOS,
  desplazar,
  diaConSemana,
  masDias,
  mesCorto,
  mesesConDatos,
  mesLargo,
  PRIMER_DIA,
  primeroDeMes,
  rotulo,
  seleccionDe,
  seleccionLibre,
  semanasDelMes,
  type Atajo,
  type Seleccion,
} from './fechas';

const DIAS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

function Mes({
  mes,
  hoy,
  inicio,
  fin,
  raros,
  alElegir,
}: {
  mes: string;
  hoy: string;
  inicio: string | null;
  fin: string | null;
  raros: ReadonlySet<string>;
  alElegir: (d: string) => void;
}) {
  return (
    <div className="w-[252px]">
      <p className="mb-2 text-center text-o-texto font-semibold">{mesLargo(mes)}</p>
      <div className="grid grid-cols-7 gap-y-1 text-center text-o-mini text-oficina-texto3">
        {DIAS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      {semanasDelMes(mes).map((semana, i) => (
        <div key={i} className="mt-1 grid grid-cols-7">
          {semana.map((d, j) => {
            if (!d) return <span key={j} />;
            const fuera = d < PRIMER_DIA || d > hoy;
            const extremo = d === inicio || d === fin;
            const dentro = inicio && fin && d > inicio && d < fin;
            return (
              <button
                key={d}
                type="button"
                disabled={fuera}
                onClick={() => alElegir(d)}
                aria-label={diaConSemana(d)}
                aria-pressed={!!extremo || !!dentro}
                className={cn(
                  'relative h-9 text-o-sub outline-none transition-colors focus-visible:ring-2 focus-visible:ring-oficina-acento',
                  fuera ? 'cursor-default text-oficina-linea' : 'hover:bg-oficina-hover',
                  dentro && 'bg-oficina-activa',
                  extremo && 'rounded-full bg-oficina-acento font-semibold text-oficina-acento-tinta hover:bg-oficina-acento-hover',
                  d === hoy && !extremo && 'font-semibold text-oficina-acento',
                )}
              >
                {Number(d.slice(8))}
                {raros.has(d) && !fuera ? <span className="absolute bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-oficina-error" aria-label={t('salaControl.fechas.diaRaro')} /> : null}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** Ancho del calendario con dos meses y los atajos, y margen mínimo con el borde de la pantalla (px). */
const ANCHO_CALENDARIO = 880;
const MARGEN_CALENDARIO = 8;

export function SelectorFechas({
  seleccion,
  hoy,
  horaHoy,
  raros = new Set(),
  alCambiar,
}: {
  seleccion: Seleccion;
  hoy: string;
  /** «hasta 09:40 UTC» cuando se ve hoy. */
  horaHoy?: string | null;
  raros?: ReadonlySet<string>;
  alCambiar: (s: Seleccion) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [inicio, setInicio] = useState<string | null>(null);
  const [fin, setFin] = useState<string | null>(null);
  const [mesVista, setMesVista] = useState(primeroDeMes(seleccion.hasta));
  const caja = useRef<HTMLDivElement>(null);
  const dialogo = useRef<HTMLDivElement>(null);
  // 22-10 (revisión de la sala): el calendario mide ~880 px y colgaba del botón hacia la derecha; dentro de la sala se
  // cortaba (a 1470 px no se veían el segundo mes ni «Aplicar»; en el móvil, casi nada). Al abrirse se coloca dentro de
  // la pantalla (posición fija, medida en ese momento) y, si no caben dos meses, enseña uno con los atajos encima.
  const [sitio, setSitio] = useState<{ left: number; top: number; ancho: number; alto: number; estrecho: boolean } | null>(null);
  // Al abrir, el foco entra en el calendario (en el primer atajo), para manejarlo con el teclado.
  useEffect(() => {
    if (abierto) dialogo.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return;
    // Escape cierra SOLO el calendario (22-10: la sala se cierra con Escape; en captura y marcado como atendido, para
    // que una pulsación no cierre las dos cosas).
    const tecla = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setAbierto(false);
    };
    const fuera = (e: MouseEvent) => caja.current && !caja.current.contains(e.target as Node) && setAbierto(false);
    window.addEventListener('keydown', tecla, true);
    window.addEventListener('mousedown', fuera);
    return () => {
      window.removeEventListener('keydown', tecla, true);
      window.removeEventListener('mousedown', fuera);
    };
  }, [abierto]);

  const abrir = () => {
    const r = caja.current?.getBoundingClientRect();
    if (r) {
      const estrecho = window.innerWidth < ANCHO_CALENDARIO + 2 * MARGEN_CALENDARIO;
      const ancho = Math.min(ANCHO_CALENDARIO, window.innerWidth - 2 * MARGEN_CALENDARIO);
      const left = Math.max(MARGEN_CALENDARIO, Math.min(r.left, window.innerWidth - ancho - MARGEN_CALENDARIO));
      const top = r.bottom + 8;
      setSitio({ left, top, ancho, alto: window.innerHeight - top - MARGEN_CALENDARIO, estrecho });
    }
    setInicio(seleccion.desde);
    setFin(seleccion.hasta);
    setMesVista(primeroDeMes(seleccion.hasta));
    setAbierto((x) => !x);
  };
  const elegirDia = (d: string) => {
    if (!inicio || fin) {
      setInicio(d);
      setFin(null);
    } else setFin(d);
  };
  const aplicar = () => {
    if (!inicio) return;
    alCambiar(seleccionLibre(inicio, fin ?? inicio, hoy));
    setAbierto(false);
  };
  const atajo = (a: Exclude<Atajo, 'fechas'>) => {
    alCambiar(seleccionDe(a, hoy));
    setAbierto(false);
  };
  const anterior = desplazar(seleccion, -1, hoy);
  const siguiente = desplazar(seleccion, 1, hoy);
  const mesAnteriorVista = primeroDeMes(masDias(mesVista, -1));
  const texto = seleccion.atajo === 'hoy' ? t('salaControl.fechas.rotuloHoy', { fecha: diaConSemana(hoy) }) : rotulo(seleccion.desde, seleccion.hasta);

  // 10-10 (Pau): con la ventana de la sala a ~830 px (portátil con la lista abierta) los atajos y el periodo partían en dos
  // filas; con estos márgenes caben en una (≈ 790 px con «Hoy, sáb 10 oct hasta 18:24 UTC»).
  return (
    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1.5">
      <div role="group" aria-label={t('salaControl.fechas.abrir')} className="flex max-w-full overflow-x-auto rounded-o-s bg-oficina-elevada p-0.5">
        {ATAJOS.map((a) => (
          <button
            key={a}
            type="button"
            aria-pressed={seleccion.atajo === a}
            title={t(`salaControl.fechas.atajosLargos.${a}` as TKey)}
            onClick={() => atajo(a as Exclude<Atajo, 'fechas'>)}
            className={cn(
              'shrink-0 whitespace-nowrap rounded-md px-2 py-1.5 text-o-sub outline-none transition-colors focus-visible:ring-2 focus-visible:ring-oficina-acento',
              seleccion.atajo === a ? 'bg-oficina-hover font-semibold text-oficina-texto' : 'text-oficina-texto2 hover:text-oficina-texto',
            )}
          >
            {t(`salaControl.fechas.atajos.${a}` as TKey)}
          </button>
        ))}
      </div>

      <div ref={caja} className="relative flex items-center rounded-o-s bg-oficina-elevada">
        <button
          type="button"
          disabled={!anterior}
          onClick={() => anterior && alCambiar(anterior)}
          aria-label={t('salaControl.fechas.anterior')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-l-o-s text-oficina-texto2 outline-none hover:bg-oficina-hover hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-acento disabled:opacity-30"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          onClick={abrir}
          aria-expanded={abierto}
          aria-haspopup="dialog"
          className="flex h-8 items-center gap-2 whitespace-nowrap border-x border-oficina-linea px-2.5 text-o-sub outline-none hover:bg-oficina-hover focus-visible:ring-2 focus-visible:ring-oficina-acento"
        >
          <CalendarDays className="h-4 w-4 text-oficina-texto2" aria-hidden />
          <span className="font-semibold">{texto}</span>
          {seleccion.atajo === 'hoy' && horaHoy ? <span className="text-o-nota text-oficina-texto3">{t('salaControl.fechas.hastaHora', { hora: horaHoy })}</span> : null}
          {seleccion.desde !== seleccion.hasta ? (
            <span className="text-o-nota text-oficina-texto3">{t('salaControl.fechas.nDias', { n: Math.round((Date.parse(seleccion.hasta) - Date.parse(seleccion.desde)) / 86_400_000) + 1 })}</span>
          ) : null}
          <ChevronDown className="h-3.5 w-3.5 text-oficina-texto3" aria-hidden />
        </button>
        <button
          type="button"
          disabled={!siguiente}
          onClick={() => siguiente && alCambiar(siguiente)}
          aria-label={t('salaControl.fechas.siguiente')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-r-o-s text-oficina-texto2 outline-none hover:bg-oficina-hover hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-acento disabled:opacity-30"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </button>

        {abierto ? (
          <div
            ref={dialogo}
            role="dialog"
            aria-label={t('salaControl.fechas.abrir')}
            style={sitio ? { left: sitio.left, top: sitio.top, maxWidth: sitio.ancho, maxHeight: sitio.alto } : undefined}
            className={cn(
              'z-50 flex overflow-auto rounded-o-m border border-oficina-linea bg-oficina-panel shadow-o-hoja',
              sitio ? 'fixed' : 'absolute left-0 top-10',
              sitio?.estrecho && 'flex-col',
            )}
          >
            <ul className={cn(sitio?.estrecho ? 'flex flex-wrap gap-1 border-b border-oficina-linea p-2' : 'w-[180px] shrink-0 border-r border-oficina-linea py-3')}>
              {ATAJOS.map((a) => (
                <li key={a}>
                  <button
                    type="button"
                    onClick={() => atajo(a as Exclude<Atajo, 'fechas'>)}
                    className={cn(
                      'text-left text-o-sub outline-none hover:bg-oficina-hover focus-visible:bg-oficina-hover',
                      sitio?.estrecho ? 'min-h-11 rounded-md px-3' : 'w-full px-4 py-1.5',
                      seleccion.atajo === a && 'font-semibold text-oficina-acento',
                    )}
                  >
                    {t(`salaControl.fechas.atajosLargos.${a}` as TKey)}
                  </button>
                </li>
              ))}
            </ul>
            <div className="p-4">
              <div className="mb-3 flex flex-wrap items-center gap-1">
                <span className="mr-1 text-o-nota text-oficina-texto3">{t('salaControl.fechas.irA')}</span>
                {mesesConDatos(hoy).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMesVista(m === primeroDeMes(PRIMER_DIA) ? primeroDeMes(masDias(m, 32)) : m)}
                    className={cn(
                      'rounded-md px-2 py-0.5 text-o-nota outline-none hover:bg-oficina-hover focus-visible:ring-2 focus-visible:ring-oficina-acento',
                      (m === mesVista || m === mesAnteriorVista) && 'bg-oficina-hover text-oficina-texto',
                    )}
                  >
                    {mesCorto(m)}
                  </button>
                ))}
              </div>
              <div className="flex items-start gap-6">
                <button type="button" onClick={() => setMesVista(primeroDeMes(masDias(mesVista, -1)))} aria-label={t('salaControl.fechas.anterior')} className="mt-1 text-oficina-texto2 hover:text-oficina-texto disabled:opacity-30" disabled={mesAnteriorVista <= primeroDeMes(PRIMER_DIA)}>
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                </button>
                {sitio?.estrecho ? null : <Mes mes={mesAnteriorVista} hoy={hoy} inicio={inicio} fin={fin} raros={raros} alElegir={elegirDia} />}
                <Mes mes={mesVista} hoy={hoy} inicio={inicio} fin={fin} raros={raros} alElegir={elegirDia} />
                <button type="button" onClick={() => setMesVista(primeroDeMes(masDias(mesVista, 32)))} aria-label={t('salaControl.fechas.siguiente')} className="mt-1 text-oficina-texto2 hover:text-oficina-texto disabled:opacity-30" disabled={mesVista >= primeroDeMes(hoy)}>
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between gap-4 border-t border-oficina-linea pt-3">
                <p className="max-w-[360px] text-o-nota leading-snug text-oficina-texto3">
                  {inicio && !fin ? t('salaControl.fechas.elegirFin') : !inicio ? t('salaControl.fechas.elegirInicio') : t('salaControl.fechas.pie')}
                </p>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => setAbierto(false)} className="rounded-o-s px-3 py-1.5 text-o-sub text-oficina-texto2 hover:bg-oficina-hover">
                    {t('salaControl.fechas.cancelar')}
                  </button>
                  <button type="button" onClick={aplicar} disabled={!inicio} className="rounded-o-s bg-oficina-acento px-4 py-1.5 text-o-sub font-semibold text-oficina-acento-tinta hover:bg-oficina-acento-hover disabled:opacity-40">
                    {t('salaControl.fechas.aplicar')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
